/**
 * Feature Flags System
 * 
 * Central location for feature flags to control rollout of new features.
 * Flags can be controlled via localStorage (dev) or environment variables (prod).
 */

/**
 * Check if UI V2 (Clubhouse 2.0) is enabled
 * 
 * UI V2 is now the default experience.
 * 
 * Priority:
 * 1. Environment variable VITE_DISABLE_UI_V2=true (opt-out)
 * 2. localStorage key "DISABLE_UI_V2"=true (opt-out)
 * 3. Default: true (enabled by default)
 * 
 * To disable in dev: localStorage.setItem("DISABLE_UI_V2", "true")
 * To disable in prod: Set VITE_DISABLE_UI_V2=true in .env
 */
export const isUIV2Enabled = (): boolean => {
  // Check if explicitly disabled via environment variable
  if (import.meta.env.VITE_DISABLE_UI_V2 === 'true') {
    return false;
  }

  // Check if explicitly disabled via localStorage (for development testing)
  if (typeof window !== 'undefined') {
    if (localStorage.getItem('DISABLE_UI_V2') === 'true') {
      return false;
    }
  }

  // Default to enabled (UI V2 is now the default)
  return true;
};

/**
 * Enable UI V2 in current browser session (removes opt-out flag)
 */
export const enableUIV2 = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('DISABLE_UI_V2');
    console.log('UI V2 (Clubhouse 2.0) enabled (default). Reload to see changes.');
  }
};

/**
 * Disable UI V2 in current browser session (opt-out)
 */
export const disableUIV2 = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('DISABLE_UI_V2', 'true');
    console.log('UI V2 (Clubhouse 2.0) disabled. Reload to see changes.');
  }
};

/**
 * Get current UI V2 status for debugging
 */
export const getUIV2Status = (): {
  enabled: boolean;
  source: 'env_disabled' | 'localStorage_disabled' | 'default_enabled';
} => {
  if (import.meta.env.VITE_DISABLE_UI_V2 === 'true') {
    return { enabled: false, source: 'env_disabled' };
  }

  if (typeof window !== 'undefined' && localStorage.getItem('DISABLE_UI_V2') === 'true') {
    return { enabled: false, source: 'localStorage_disabled' };
  }

  return { enabled: true, source: 'default_enabled' };
};

/**
 * Get a feature flag value from server-side environment variables
 * @param name - The environment variable name
 * @param defaultVal - Default value if env var is not set
 * @returns The environment variable value or default
 */
export function getServerFlag(name: string, defaultVal?: string | boolean): string | boolean | undefined {
  if (typeof window !== 'undefined') {
    throw new Error('getServerFlag() can only be used on the server');
  }
  
  const value = (import.meta.env as any)[name];
  if (value === undefined) {
    return defaultVal;
  }
  
  // Convert string booleans to actual booleans
  if (value === 'true') return true;
  if (value === 'false') return false;
  
  return value;
}

/**
 * Get a feature flag value from client-side environment variables (VITE_*)
 * @param name - The environment variable name (with or without VITE_ prefix)
 * @param defaultVal - Default value if env var is not set
 * @returns The environment variable value or default
 */
export function getClientFlag(name: string, defaultVal?: string | boolean): string | boolean | undefined {
  // Ensure we're checking VITE_ prefixed vars on client
  const envName = name.startsWith('VITE_') ? name : `VITE_${name}`;
  
  // Use import.meta.env for Vite
  const value = import.meta.env[envName];
  
  if (value === undefined) {
    return defaultVal;
  }
  
  // Convert string booleans to actual booleans
  if (value === 'true') return true;
  if (value === 'false') return false;
  
  return value;
}

// Coach Dubs realtime feature removed for MVP focus

// Payments placeholder flag (Step 24)
// Server: process.env.ENABLE_PAYMENTS
// Client: import-time var VITE_ENABLE_PAYMENTS (mirrored by build tooling)
// Note: server-only; do not import into client bundles
export function isPaymentsEnabledServer(): boolean {
  return getServerFlag('ENABLE_PAYMENTS', false) === true;
}

export function isPaymentsEnabledClient(): boolean {
  return getClientFlag('VITE_ENABLE_PAYMENTS', false) === true;
}

// Unified helper (SSR + Client) — mirrors isRealtimeEnabled()
export function isPaymentsEnabled(): boolean {
  // Server-side (SSR / edge / node)
  if (typeof window === 'undefined') {
    return isPaymentsEnabledServer();
  }
  // Client-side (browser)
  return isPaymentsEnabledClient();
}

// S-2201: Entitlements gating flag for MVP mode
export function isEntitlementsGatingEnabled(): boolean {
  // Server-side (SSR / edge / node)
  if (typeof window === 'undefined') {
    return getServerFlag('ENABLE_ENTITLEMENTS_GATING', false) === true;
  }
  // Client-side (browser) - default to false for MVP
  return getClientFlag('VITE_ENABLE_ENTITLEMENTS_GATING', false) === true;
}

// S-2216: MVP entitlements bypass flag for unlimited uploads
export function isMvpEntitlementsOff(): boolean {
  // Server-side (SSR / edge / node)
  if (typeof window === 'undefined') {
    return getServerFlag('MVP_ENTITLEMENTS_OFF', true) === true;
  }
  // Client-side (browser) - default to true for MVP (unlimited uploads)
  return getClientFlag('VITE_MVP_ENTITLEMENTS_OFF', true) === true;
}

/**
 * Check if MVP Navigation is enabled (MVP V3 Pivot)
 * 
 * Controls whether the new MVP navigation (Home/Drills/Upload/Progress) is active.
 * 
 * Priority:
 * 1. Environment variable VITE_MVP_NAV_ENABLED=false (opt-out)
 * 2. localStorage key "MVP_NAV_ENABLED"=false (dev opt-out)
 * 3. Default: true (MVP navigation is now default)
 * 
 * To disable: Set VITE_MVP_NAV_ENABLED=false in .env or localStorage.setItem("MVP_NAV_ENABLED", "false")
 */
export function isMvpNavEnabled(): boolean {
  // Check environment variable first (production opt-out)
  if (import.meta.env?.VITE_MVP_NAV_ENABLED === 'false') {
    return false;
  }

  // Check localStorage (development/testing opt-out)
  if (typeof window !== 'undefined') {
    if (localStorage.getItem('MVP_NAV_ENABLED') === 'false') {
      return false;
    }
  }

  // Default to true (MVP navigation is now default)
  return true;
}
